import type {
  Complement,
  ProjectItem,
} from '@customink/project-service-react-components';
import {
  mediaQueries,
  useBreakpointName,
} from '@customink/project-service-react-components';
import Box from '@mui/material/Box';

import ComplementCard from './ComplementCard';
import CatalogCard from '../CatalogCard';

import { useFilteredComplements } from '@/hooks/Product/useFilteredComplements';
import { spacing8 } from '@/utils/designTokens';

type ProjectComplementsProps = {
  onAddCompatible?: () => void;
  onSelectComplement?: (complement: Complement) => void;
  projectItems: ProjectItem[];
  showCatalogCard?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const defaultEmptyHandler = () => {};

export default function ComplementsGrid({
  onAddCompatible = defaultEmptyHandler,
  onSelectComplement = defaultEmptyHandler,
  projectItems,
  showCatalogCard = false,
}: ProjectComplementsProps) {
  const anchorProjectItem = projectItems[0];
  const filteredComplementaryProducts = useFilteredComplements(projectItems);

  const breakpointName = useBreakpointName();

  if (!filteredComplementaryProducts?.length) return null;

  let complementsDisplayLimit = {
    xs: 5,
    sm: 10,
    md: 6,
    lg: 8,
    xl: 8,
    xxl: 10,
  }[breakpointName];

  const complementsGridSx = {
    display: 'grid',
    gridGap: spacing8,
    gridTemplateColumns: '1fr',
    [mediaQueries.sm]: { gridTemplateColumns: 'repeat(2, 1fr)' },
    [mediaQueries.md]: { gridTemplateColumns: 'repeat(3, 1fr)' },
    [mediaQueries.lg]: { gridTemplateColumns: 'repeat(4, 1fr)' },
    [mediaQueries.xl]: { gridTemplateColumns: 'repeat(4, 1fr)' },
    [mediaQueries.xxl]: { gridTemplateColumns: 'repeat(5, 1fr)' },
  };

  if (showCatalogCard) {
    complementsDisplayLimit -= 1;
  }
  const limitedComplementaryProducts = filteredComplementaryProducts.slice(
    0,
    complementsDisplayLimit,
  );

  return (
    <Box sx={complementsGridSx}>
      {limitedComplementaryProducts?.map((complementaryProduct) => (
        <ComplementCard
          colorId={complementaryProduct.colorId}
          designCid={anchorProjectItem.design?.cid || ''}
          key={complementaryProduct.colorId}
          onClick={onSelectComplement}
        />
      ))}
      {showCatalogCard && (
        <CatalogCard
          designCid={anchorProjectItem.design?.cid}
          onClick={onAddCompatible}
        />
      )}
    </Box>
  );
}
